<template>
  <div>
    <div
      v-if="canCreateManual"
      class="d-flex justify-end mb-2"
    >
      <v-btn
        small
        outlined
        color="primary"
        :disabled="!canManage"
        @click="$emit('create', account)"
      >
        <v-icon left small>mdi-plus</v-icon>
        Novo template
      </v-btn>
    </div>

    <v-alert
      v-if="missingRequired.length"
      type="warning"
      text
      dense
      class="mb-2"
    >
      Template(s) obrigatório(s) não provisionado(s):
      {{ missingRequired.map(typeTitle).join(", ") }}
    </v-alert>

    <v-simple-table v-if="templates.length" dense>
      <template v-slot:default>
        <thead>
          <tr>
            <th>Template</th>
            <th>Status</th>
            <th class="text-right">Ações</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="template in templates" :key="template.id">
            <td>
              <div class="d-flex align-center flex-wrap gap-1">
                <span>{{ typeTitle(template.type) }}</span>
                <v-chip
                  v-if="isRequiredType(template.type)"
                  x-small
                  label
                  color="primary"
                  outlined
                >
                  Obrigatório
                </v-chip>
              </div>
              <div class="text-caption text--secondary">{{ template.type }}</div>
            </td>
            <td>
              <v-chip :color="statusColor(template.status)" label x-small dark>
                {{ statusLabel(template.status) }}
              </v-chip>
            </td>
            <td class="text-right text-nowrap">
              <v-btn
                v-if="canSyncStatus(template)"
                icon
                small
                color="info"
                :loading="syncingId === template.id"
                :disabled="!canManage"
                title="Atualizar status"
                @click="$emit('sync-status', template)"
              >
                <v-icon small>mdi-sync</v-icon>
              </v-btn>
              <v-btn
                v-if="canSubmit(template)"
                icon
                small
                color="primary"
                :loading="submittingId === template.id"
                :disabled="!canManage"
                title="Submeter"
                @click="$emit('submit', template)"
              >
                <v-icon small>mdi-send</v-icon>
              </v-btn>
              <v-btn
                v-if="canEdit(template)"
                icon
                small
                :disabled="!canManage"
                title="Editar"
                @click="$emit('edit', template)"
              >
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
              <v-btn
                v-if="canDelete(template)"
                icon
                small
                color="error"
                :disabled="!canManage"
                title="Remover"
                @click="$emit('delete', template)"
              >
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>

    <v-alert v-else type="info" text dense class="mb-0">
      Nenhum template cadastrado para esta conta.
    </v-alert>
  </div>
</template>

<script>
const STATUS = {
  DRAFT: { label: "Rascunho", color: "grey" },
  PENDING: { label: "Em análise", color: "warning" },
  APPROVED: { label: "Aprovado", color: "success" },
  DENIED: { label: "Recusado", color: "error" },
};

export default {
  name: "AccountTemplatesTable",
  props: {
    account: { type: Object, required: true },
    templates: { type: Array, default: () => [] },
    templateTypes: { type: Array, default: () => [] },
    canManage: { type: Boolean, default: false },
    submittingId: { type: String, default: null },
    syncingId: { type: String, default: null },
  },
  computed: {
    canCreateManual() {
      return this.account.type !== "META_WHATSAPP";
    },
    requiredTypesForAccount() {
      const channel = this.accountChannel(this.account.type);
      return this.templateTypes.filter(
        (t) => t.required && t.channel === channel && t.meta
      );
    },
    missingRequired() {
      if (this.account.type !== "META_WHATSAPP") return [];
      return this.requiredTypesForAccount
        .filter((t) => !this.templates.some((tpl) => tpl.type === t.key))
        .map((t) => t.key);
    },
  },
  methods: {
    accountChannel(type) {
      if (type === "SMTP_EMAIL") return "EMAIL";
      return "WHATSAPP";
    },
    statusLabel(s) {
      return STATUS[s]?.label || s;
    },
    statusColor(s) {
      return STATUS[s]?.color || "grey";
    },
    typeTitle(key) {
      const t = this.templateTypes.find((x) => x.key === key);
      return t ? t.title : key;
    },
    isRequiredType(typeKey) {
      const t = this.templateTypes.find((x) => x.key === typeKey);
      return !!t?.required;
    },
    canEdit(template) {
      return template.status === "DRAFT" || template.status === "DENIED";
    },
    canSubmit(template) {
      return (
        this.account.type === "META_WHATSAPP" &&
        (template.status === "DRAFT" || template.status === "DENIED")
      );
    },
    canSyncStatus(template) {
      return (
        this.account.type === "META_WHATSAPP" && template.status === "PENDING"
      );
    },
    canDelete(template) {
      return !this.isRequiredType(template.type);
    },
  },
};
</script>

<style scoped>
.gap-1 {
  gap: 4px;
}
</style>
